import { DateFormatter } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { isAfter } from 'date-fns'
import { TFunction } from 'i18next'

import { ComputeTripsMinMaxCapacityData } from '../../../../api/centers/compute-trips-min-max-capacity'
import { getTranslatedName } from '../../../utils/functions/translation.functions'
import {
  LocalOrganizerTypeEnum,
  localOrganizerTypeEnumMapping
} from '../../enums/center/locale-organizer'
import { FamilyArrangementEnum } from '../../enums/ol/family-arrangement.enum'
import { ProductTypeEnum, productTypeEnumMapping } from '../../enums/product/product-type.enum'
import {
  getOlStaffQualificationLabel,
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../enums/staff/staff-qualifications.enum'
import { CenterToJourneyInformation } from '../center-to-journey/center-to-journey'
import { CenterToJourneyInput } from '../center-to-journey/center-to-journey-input'
import { CenterToJourneyProperties } from '../center-to-journey/properties.center-to-journey'
import { OlJourneyPlace } from '../ol/ol-journey-place'
import { Option } from '../options/option'
import { PeriodTypes } from '../period/period'
import { Rad } from '../rad/rad'
import { StaffRequestInput } from '../staff-request/staff-request-input'
import { TripProperties } from '../trip/properties.trip'
import { TripPlaceToCenterRequestInput } from '../trip-place-to-center-request/trip-place-to-center-request-input'
import { Center, CenterLight } from './center'
import {
  AssignJourneyToCenterInput,
  AssignJourneyToCenterRequestInput,
  CenterInput
} from './center-input'

export class CenterProperties {
  static checkIfStaffRequestsAreEmpty(staffRequests: StaffRequestInput[]) {
    if (staffRequests.length === 0)
      return "Veuillez préciser le nombre d'encadrants nécessaire et leurs qualifications"
    if (
      staffRequests.some(
        (staffRequest) => !!staffRequest.qualification && staffRequest.numberOfStaff === 0
      )
    )
      return "Veuillez préciser le nombre d'encadrants nécessaire \n et leurs qualifications"
  }

  /**
   * takes the computes result for min and max capacity for trip and return the max and min capacity
   * based on the trip id
   * @param data  TripPlaceToCenterRequestInput[]
   * @param tripId  number
   * @returns   { minNumberOfStudents: number, maxNumberOfStudents: number }
   * @throws Error
   * @example
   * const data = [
   *  { tripId: 1, minNumberOfStudents: 10, maxNumberOfStudents: 20 },
   * { tripId: 2, minNumberOfStudents: 15, maxNumberOfStudents: 25 }
   * ]
   * const tripId = 1
   * const result = CenterProperties.getMinMaxCapacityForTrip(data, tripId)
   * result // { minNumberOfStudents: 10, maxNumberOfStudents: 20 }
   */
  static getMinMaxCapacityForTrip(
    data: ComputeTripsMinMaxCapacityData[],
    tripId: number
  ): { minNumberOfStudents: number; maxNumberOfStudents: number } {
    const trip = data.find((trip) => trip.tripId === tripId)
    if (!trip) throw new Error(`Trip with id ${tripId} not found`)
    const max = data.find((trip) => trip.tripId === tripId)?.maxCapacity ?? 0
    const min = data.find((trip) => trip.tripId === tripId)?.minCapacity ?? 0
    return { minNumberOfStudents: min, maxNumberOfStudents: max }
  }

  /**
   *  Check if the number of students requested is within the trip capacity
   * @param data    TripPlaceToCenterRequestInput
   * @param minNumberOfStudents  number
   * @param maxNumberOfStudents   number
   * @returns   string | undefined
   */
  static checkNumberOfStudentsAccordingToTripCapacity(
    data: TripPlaceToCenterRequestInput,
    minNumberOfStudents: number,
    maxNumberOfStudents: number
  ): string | undefined {
    if (data.numberOfStudentsPlacesRequested < minNumberOfStudents) {
      return `Le nombre de jeunes demandé est inférieur au minimum de ${minNumberOfStudents} jeunes`
    }
    if (data.numberOfStudentsPlacesRequested > maxNumberOfStudents) {
      return `Le nombre de jeunes demandé est supérieur au maximum de ${maxNumberOfStudents} jeunes`
    }
  }

  private static calculateRequiredStaff(numberOfStudents: number): number {
    return Math.max(1, Math.ceil(numberOfStudents / 15))
  }

  static validateTripRatios(
    data: TripPlaceToCenterRequestInput[],
    centerToJourneys: CenterToJourneyInput[]
  ): string | undefined {
    for (const centerToJourney of centerToJourneys) {
      const relatedTripsIds = new Set(centerToJourney.relatedTrips.map((trip) => trip.id))
      const relatedTrips = data.filter(({ trip }) => relatedTripsIds.has(trip.id))
      if (!relatedTrips.length) continue

      // Only validate trips that have staff defined (not undefined)
      const tripsRequiringValidation = relatedTrips.filter(
        ({ numberOfStaffPlacesRequested, disableRestriction }) =>
          numberOfStaffPlacesRequested !== undefined && !disableRestriction
      )

      if (!tripsRequiringValidation.length) continue

      // Count students and staff ONLY from trips with defined staff
      const numberOfStudents = tripsRequiringValidation.reduce(
        (acc, trip) => acc + trip.numberOfStudentsPlacesRequested,
        0
      )
      const numberOfStaff = tripsRequiringValidation.reduce(
        (acc, trip) => acc + (trip.numberOfStaffPlacesRequested ?? 0),
        0
      )

      const requiredStaff = this.calculateRequiredStaff(numberOfStudents)
      if (numberOfStudents > 0 && numberOfStaff < requiredStaff) {
        return `Le nombre d'encadrants demandé (${numberOfStaff}) est insuffisant pour le nombre de jeunes (${numberOfStudents}) sur un même séjour. Il faut au moins ${requiredStaff} encadrants pour respecter le ratio de 1 encadrant pour 15 jeunes.`
      }
    }
  }

  /**
   * Check if the number of staff requested is within the maximum number of staff
   * and if the number of staff requested meets the 1:15 ratio
   * @param data  TripPlaceToCenterRequestInput[]
   * @param totalNumberOfStudents number
   * @param totalNumberOfStaffs number
   * @returns string | undefined
   */
  static checkCenterTrips(
    data: TripPlaceToCenterRequestInput[],
    centerToJourneys: CenterToJourneyInput[],
    totalNumberOfStudents: number,
    totalNumberOfStaffs: number
  ): string | undefined {
    // If there is at least one trip with 0 students places requested, return an error
    if (data.some((trip) => trip.numberOfStudentsPlacesRequested === 0))
      return 'Veuillez préciser la répartition des jeunes et des encadrants dans les différents voyages sélectionnés'

    // If there is at least one trip with students places requested and no staff places
    // requested and it is not disabled restriction, return an error
    if (
      data.some(
        (trip) =>
          trip.numberOfStudentsPlacesRequested > 0 &&
          trip.numberOfStaffPlacesRequested === 0 &&
          !trip.disableRestriction
      )
    )
      return 'Veuillez préciser la répartition des jeunes et des encadrants dans les différents voyages sélectionnés'

    const numberOfStaffPlacesRequested = data.reduce(
      (acc, trip) => acc + (trip.numberOfStaffPlacesRequested ?? 0),
      0
    )
    const numberOfStudentsPlacesRequested = data.reduce(
      (acc, trip) => acc + trip.numberOfStudentsPlacesRequested,
      0
    )

    // if their is a no student places requested
    if (numberOfStudentsPlacesRequested === 0)
      return 'Veuillez préciser la répartition des jeunes et des encadrants dans les différents voyages sélectionnés'

    if (numberOfStudentsPlacesRequested !== totalNumberOfStudents) {
      const diff = numberOfStudentsPlacesRequested - totalNumberOfStudents
      return diff > 0
        ? `Le nombre maximal total de jeunes n'est pas égal à la somme (${diff} en moins)`
        : `Le nombre maximal total de jeunes n'est pas égal à la somme (${Math.abs(diff)} en plus)`
    }

    if (numberOfStaffPlacesRequested !== totalNumberOfStaffs) {
      const diff = numberOfStaffPlacesRequested - totalNumberOfStaffs
      return diff > 0
        ? `Le nombre total d’encadrants n'est pas égal à la somme (${diff} en moins)`
        : `Le nombre total d’encadrants n'est pas égal à la somme (${Math.abs(diff)} en plus)`
    }

    return this.validateTripRatios(data, centerToJourneys)
  }

  /**
   *  Get the total number of staffs
   * @param data  StaffRequestInput[]
   * @returns   number
   */
  static getTotalNumberOfStaffs(data?: StaffRequestInput[]): number {
    if (!data) return 0
    return data.reduce((acc, staffRequest) => acc + staffRequest.numberOfStaff, 0)
  }

  /**
   *  Get the statistics of French qualification staffs and the total number of French staffs
   * @param data  StaffRequestInput[]
   * @returns   { frenchStatistics: Partial<Record<StaffQualificationEnum, number>>[], frenchTotal: number }
   */
  static getFrenchQualificationStaffsStatistics(data: StaffRequestInput[]): {
    frenchStatistics: Partial<Record<StaffQualificationEnum, number>>[]
    frenchTotal: number
  } {
    if (this.checkIfStaffRequestsAreEmpty(data)) return { frenchStatistics: [], frenchTotal: 0 }
    const frenchStatistics = data
      .map((staffRequest) => {
        const qualification = staffRequest.qualification // Access the only element

        if (qualification && staffQualificationEnumMapping[qualification].type === 'french') {
          return { [qualification]: staffRequest.numberOfStaff } // Return as a Record
        }

        return null // Filter out non-French qualifications
      })
      .filter((entry): entry is Record<StaffQualificationEnum, number> => entry !== null) // Remove nulls

    const frenchTotal = frenchStatistics.reduce((acc, record) => acc + Object.values(record)[0], 0)
    return { frenchStatistics, frenchTotal }
  }

  /**
   *  Get the statistics of foreign qualification staffs and the total number of foreign staffs
   * @param data  StaffRequestInput[]
   * @returns  { foreignStatistics: Partial<Record<StaffQualificationEnum, number>>[], foreignTotal: number }
   */
  static getForeignQualificationStaffsStatistics(data: StaffRequestInput[]): {
    foreignStatistics: Partial<Record<StaffQualificationEnum, number>>[]
    foreignTotal: number
  } {
    if (this.checkIfStaffRequestsAreEmpty(data)) return { foreignStatistics: [], foreignTotal: 0 }
    const foreignStatistics = data
      .map((staffRequest) => {
        const qualification = staffRequest.qualification // Access the only element

        if (qualification && staffQualificationEnumMapping[qualification].type === 'foreign') {
          return { [qualification]: staffRequest.numberOfStaff } // Return as a Record
        }

        return null // Filter out non-French qualifications
      })
      .filter((entry): entry is Record<StaffQualificationEnum, number> => entry !== null) // Remove nulls

    const foreignTotal = foreignStatistics.reduce(
      (acc, record) => acc + Object.values(record)[0],
      0
    )
    return { foreignStatistics, foreignTotal }
  }

  // This function is used in the CenterJourneySelectionList component
  // to check if there are any errors in the form
  // based on the current step and the form data
  // It returns an error message if there are any errors
  // or undefined if there are no errors
  static checkPossibleErrorsPerStep = (
    step: number,
    data: AssignJourneyToCenterInput
  ): string | undefined => {
    switch (step) {
      case 0:
        return undefined
      case 1:
        return CenterToJourneyProperties.checkIfTripsAndNumberOfChildrenAreEmpty(
          data.centerToJourneys
        )
      case 2:
        return this.checkIfStaffRequestsAreEmpty(data.staffRequests ?? [])
      case 3:
        return this.checkCenterTrips(
          data.tripPlacesRequests,
          data.centerToJourneys,
          CenterToJourneyProperties.getTotalNumberOfChildren(data.centerToJourneys),
          this.getFrenchQualificationStaffsStatistics(data.staffRequests).frenchTotal
        )
      default:
        return undefined
    }
  }

  // This function is used in the CenterTripsInformation component
  // to check if there are any errors in the form
  // based on the form data
  // It returns an error message if there are any errors
  // or undefined if there are no errors
  static getModalTitleBasedOnStep(index: number): string {
    switch (index) {
      case 0:
        return 'Assigner un séjour'
      case 1:
        return 'Sélectionner le nombre max de jeunes et les voyages par séjour'
      case 2:
        return "Préciser le nombre d'encadrants nécessaire"
      case 3:
        return 'Préciser la répartition des jeunes et des encadrants dans les différents voyages sélectionnés'
      default:
        return 'Étape inconnue'
    }
  }

  // One entry per transport means, e.g. "Avion (Paris, Lyon), Train": flights also list their
  // departure towns since the town is what tells the group's flights apart
  static getMainTripsTransportMean(center: Center): string {
    const departuresByTransport = new Map<string, Set<string>>()

    for (const { trip } of center.tripPlacesRequests) {
      const transportName = TripProperties.handleTransportMeansName(trip.transportMeans)
      const departures = departuresByTransport.get(transportName) ?? new Set<string>()
      const departure = getTranslatedName(trip.departure)
      if (TripProperties.isFlight(trip.transportMeans) && departure) departures.add(departure)
      departuresByTransport.set(transportName, departures)
    }

    return Array.from(departuresByTransport.entries())
      .map(([transportName, departures]) =>
        departures.size ? `${transportName} (${Array.from(departures).join(', ')})` : transportName
      )
      .join(', ')
  }

  static mapAssignJourneyToCenterToRequestInput(
    assignJourneyToCenter: AssignJourneyToCenterInput
  ): AssignJourneyToCenterRequestInput {
    return {
      staffRequests: assignJourneyToCenter.staffRequests,
      tripPlacesRequests: assignJourneyToCenter.tripPlacesRequests.map((tripPlaceRequest) => ({
        numberOfStaffPlacesRequested: tripPlaceRequest.numberOfStaffPlacesRequested,
        numberOfStudentsPlacesRequested: tripPlaceRequest.numberOfStudentsPlacesRequested,
        disableRestriction: tripPlaceRequest.disableRestriction,
        tripId: tripPlaceRequest.trip.id
      })),
      centerToJourneys: assignJourneyToCenter.centerToJourneys.map((centerToJourney) => ({
        journeyId: centerToJourney.journey.id,
        maxNumberOfStudentPlaces: centerToJourney.maxNumberOfStudentPlaces,
        relatedTrips: centerToJourney.relatedTrips.map((trip) => trip.id)
      }))
    }
  }

  static mapCenterToInput(center: Center): CenterInput {
    return {
      assignJourneyToCenter: {
        centerToJourneys: center.centerToJourneys.map((centerToJourney) => ({
          journey: centerToJourney.journey,
          maxNumberOfStudentPlaces: centerToJourney.maxNumberOfStudentPlaces,
          relatedTrips: centerToJourney.relatedTrips
        })),
        staffRequests: center.staffRequests
          .filter((staffRequest) => staffRequest.isSpecialRequest === false)
          .map((staffRequest) => ({
            numberOfStaff: staffRequest.numberOfStaff,
            qualification: staffRequest.qualification,
            isSpecialRequest: staffRequest.isSpecialRequest
          })),
        tripPlacesRequests: center.tripPlacesRequests.map((tripPlaceRequest) => ({
          numberOfStaffPlacesRequested: tripPlaceRequest.numberOfStaffPlacesRequested,
          numberOfStudentsPlacesRequested: tripPlaceRequest.numberOfStudentsPlacesRequested,
          disableRestriction: tripPlaceRequest.disableRestriction,
          trip: tripPlaceRequest.trip
        }))
      },
      lecResponsableId: center.lecResponsableId,
      name: center.name,
      olId: center.olId,
      maxAge: center.maxAge,
      maxSchoolLevel: center.maxSchoolLevel,
      minAge: center.minAge,
      minSchoolLevel: center.minSchoolLevel,
      olJourneyPlaceId: center.olJourneyPlaceId,
      journeyPlaceId: center.journeyPlaceId,
      retainerAmount: center.retainerAmount
    }
  }

  static getRadNameForRequestStaff(rad?: Rad): string {
    if (!rad) return ''
    const { title, date, hours, addressName } = rad
    const dateTime = `${DateFormatter.DDMMYYYY(date)} ${DateFormatter.getFormattedTime(hours)}`
    return `${title} - ${dateTime} - ${addressName}`
  }

  static getAvailablePlaces(center: CenterLight): number {
    return (
      (center?.maxNumberOfPlaces ?? 0) -
      (center?.affectedStudentsNumber ?? 0) -
      (center?.blockedPlacesNumber ?? 0)
    )
  }

  /**
   * The center stay dates come from the denormalised `startDate` / `endDate` columns, which the
   * backend keeps in sync with the min departure date and max return date of its journeys.
   * The returned keys stay named `departureDate` / `returnDate` for the existing callers.
   */
  static getDates(center: { startDate?: string | Date; endDate?: string | Date }) {
    if (!center.startDate || !center.endDate) return undefined

    return { departureDate: new Date(center.startDate), returnDate: new Date(center.endDate) }
  }

  static getCenterDates(
    center: { startDate?: string | Date; endDate?: string | Date },
    options?: { i18n?: boolean }
  ): string | undefined {
    const dates = this.getDates(center)
    if (!dates) return undefined
    const { departureDate, returnDate } = dates

    return `${DateFormatter.DDMMYYYY(departureDate, options)} - ${DateFormatter.DDMMYYYY(returnDate, options)}`
  }

  static getDurationInDays = (date1: string | Date, date2: string | Date) =>
    Math.floor((new Date(date2).getTime() - new Date(date1).getTime()) / (1000 * 3600 * 24)) + 1

  static getNightDurationInDays = (date1: string | Date, date2: string | Date) =>
    this.getDurationInDays(date1, date2) - 1

  static centerNightsCount(center: {
    startDate?: string | Date
    endDate?: string | Date
  }): number | undefined {
    const dates = this.getDates(center)
    if (!dates) return undefined
    return this.getNightDurationInDays(dates.departureDate, dates.returnDate)
  }

  static getCenterFormulaName(center: Center): string[] {
    return center.centerToJourneys
      .map((centerToJourney) => CenterToJourneyProperties.getFormulaName(centerToJourney))
      .flat()
      .filter((value, index, self) => self.indexOf(value) === index)
  }

  static getCenterThemeName(center: {
    centerToJourneys: {
      journey: {
        mainOption: Pick<Option, 'translations'>
      }
    }[]
  }): string[] {
    if (center.centerToJourneys && center.centerToJourneys.length === 0) return []
    return center.centerToJourneys
      .map((centerToJourney) => CenterToJourneyProperties.getThemeName(centerToJourney))
      .flat()
      .filter((value, index, self) => self.indexOf(value) === index)
  }

  static getCenterOptions(center: {
    centerToJourneys: {
      journey: {
        mainOption: Pick<Option, 'translations'>
        secondaryOptions: { option: Pick<Option, 'translations'> }[]
      }
    }[]
  }): string[] {
    return center.centerToJourneys
      .map((centerToJourney) => CenterToJourneyProperties.getOptions(centerToJourney))
      .flat()
      .filter((value, index, self) => self.indexOf(value) === index)
  }

  /**
   *  Get the center event dates and return the period
   * @param center  - The center object containing journey data
   * @returns   An object with period
   */
  static getCenterJourneyInformation(
    center: { centerToJourneys: CenterToJourneyInformation[] },
    { includeNumberOfHoursPerWeek }: { includeNumberOfHoursPerWeek?: boolean } = {
      includeNumberOfHoursPerWeek: true
    }
  ): {
    title: string
    period?: PeriodTypes.Basic
    journeyId?: number
  }[] {
    return center.centerToJourneys.map((centerToJourney) => {
      const journey = centerToJourney.journey
      const date = `${DateFormatter.DDMMYYYY(journey.date.departureDate)} à ${DateFormatter.DDMMYYYY(journey.date.returnDate)}`
      const secondaryOptions = journey.secondaryOptions
        .map((secondaryOption) => getTranslatedName(secondaryOption.option))
        .join(' , ')

      const numberOfHoursPerWeek =
        includeNumberOfHoursPerWeek && journey.date.product.type === ProductTypeEnum.ONE_TO_ONE
          ? journey.date.product.numberOfHoursPerWeek
          : undefined

      return {
        title: `${journey.reference} - ${date} - ${getTranslatedName(journey.mainOption)}  ${secondaryOptions ? ` - ${secondaryOptions}` : ''}${numberOfHoursPerWeek ? ` - ${numberOfHoursPerWeek}h de cours / sem` : ''}`,
        period: journey.date.period ?? undefined,
        journeyId: centerToJourney.journeyId
      }
    })
  }

  /*
   *  Check if the history of assignment is valid by comparing the updatedAt date with the journey date.
   * @param updatedAt   The date when the host family was last updated.
   * @param journeyDate   The date of the journey.
   * @returns   A boolean indicating whether the history of assignment is valid.
   */
  static checkHistoryOfAssignmentByJourneyDate = (
    updatedAt?: Date,
    journeyDate?: string | Date
  ) => {
    if (!updatedAt || !journeyDate) return false

    const updatedAtDate = new Date(updatedAt)
    const journeyDateDate = new Date(journeyDate)

    return isAfter(updatedAtDate, journeyDateDate)
  }

  static getCenterOrganizerLabel = (
    input: {
      type: LocalOrganizerTypeEnum
      familyArrangement: FamilyArrangementEnum
      qualification?: StaffQualificationEnum
      numberOfHoursPerWeek?: number
      productType?: ProductTypeEnum
    },
    t: TFunction<AvailableLanguages, undefined>,
    options?: {
      showHours?: boolean
    }
  ): string => {
    const { type, qualification, familyArrangement, numberOfHoursPerWeek, productType } = input
    const baseLabel = localOrganizerTypeEnumMapping[type]
    const qualLabel = qualification ? t(getOlStaffQualificationLabel(qualification)) : null
    // The backend only sets the formula on the rows of a center mixing Club 4 and One to One stays
    const formulaLabel = productType ? t(productTypeEnumMapping[productType].title) : null
    const hoursLabel =
      options?.showHours && numberOfHoursPerWeek ? `${numberOfHoursPerWeek}h/week` : null
    return [qualLabel ?? baseLabel, formulaLabel, familyArrangement, hoursLabel]
      .filter(Boolean)
      .join(' ')
  }

  static getCenterTeachingAddress = (center: {
    olJourneyPlace?: Pick<
      NonNullable<Center['olJourneyPlace']>,
      'isSameAddresForTuition' | 'address1' | 'tuitionAddress1'
    >
  }): string | undefined => {
    const place = center.olJourneyPlace
    if (!place) return undefined
    if (place.isSameAddresForTuition) return center.olJourneyPlace?.address1
    return place.tuitionAddress1
  }

  static getCenterTeachingCompleteAddress = (
    olJourneyPlace?: OlJourneyPlace,
    toCountryName: (country?: string) => string | undefined = (country) => country
  ) => {
    if (!olJourneyPlace) return undefined

    const formatAddress = (
      address1?: string,
      address2?: string,
      zipCode?: string,
      city?: string,
      country?: string
    ) => {
      const cityLine = [zipCode, city].filter(Boolean).join(' ')
      return [address1, address2, cityLine, country].filter(Boolean).join(', ')
    }

    if (olJourneyPlace.isSameAddresForTuition) {
      return {
        name: olJourneyPlace.name,
        address: formatAddress(
          olJourneyPlace.address1,
          olJourneyPlace.address2,
          olJourneyPlace.zipCode,
          olJourneyPlace.city,
          toCountryName(olJourneyPlace.country)
        ),
        address1: olJourneyPlace.address1,
        address2: olJourneyPlace.address2,
        zipCode: olJourneyPlace.zipCode,
        city: olJourneyPlace.city,
        country: olJourneyPlace.country
      }
    }

    return {
      name: olJourneyPlace.tuitionName,
      address: formatAddress(
        olJourneyPlace.tuitionAddress1,
        olJourneyPlace.tuitionAddress2,
        olJourneyPlace.tuitionZipCode,
        olJourneyPlace.tuitionCity,
        toCountryName(olJourneyPlace.tuitionCountry)
      ),
      address1: olJourneyPlace.tuitionAddress1,
      address2: olJourneyPlace.tuitionAddress2,
      zipCode: olJourneyPlace.tuitionZipCode,
      city: olJourneyPlace.tuitionCity,
      country: olJourneyPlace.tuitionCountry
    }
  }

  /** Every product type of the center: a center can mix Club 4 and One to One stays. */
  static getCenterProductTypes(center: Center): ProductTypeEnum[] {
    return [
      ...new Set(
        (center.centerToJourneys ?? []).map(
          (centerToJourney) => centerToJourney.journey.date.product.type
        )
      )
    ]
  }
}
